import { useStore } from '@vromo/mobx-react/mobx-react';
import Button from '@vromo/ui/components/Button';
import ConditionalTooltip from '@vromo/ui/components/ConditionalTooltip';
import Container from '@vromo/ui/components/Container';
import Flex from '@vromo/ui/components/Flex';
import OverlayablePanel from '@vromo/ui/components/Overlay';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { RootStore } from '../stores/RootStore';
import ChooseActivePartnerships from './components/ChooseActivePartnerships';
import CompletePage from './components/CompletePage';
import FleetCoverageDetailsModal from './components/FleetCoverageDetailsModal';
import HeaderContent from './components/HeaderContent';
import HelpSection from './components/HelpSection';
import RestaurantResult from './components/RestaurantResult';
import styles from './findFleets.style';
import { fetchAvailability, getNewRestaurant, submitForm } from './FleetHelper';
import { AcceptedFleetOptions, FleetCoverageDetails, RestaurantType } from './interfaces';

const FindFleets: FC = () => {
    const {
        authStore: { user, role, graph }
    } = useStore<RootStore>('rootStore');
    const { t } = useTranslation();

    const [restaurants, setRestaurants] = useState<RestaurantType[]>([]);
    const [editedRestaurants, setEditedRestaurants] = useState<string[]>([]);
    const [acceptedFleetOptions, setAcceptedFleetOptions] = useState<AcceptedFleetOptions[]>([]);
    const [existingProviders, setExistingProviders] = useState<string[]>([]);

    const [detailsItem, setDetailsItem] = useState<FleetCoverageDetails | null>(null);
    const [isShowingDetails, setShowingDetails] = useState<boolean>(false);
    const [showCompletePage, setShowCompletePage] = useState<boolean>(false);
    const [isFormValidated, setIsFormValidated] = useState<boolean>(false);
    const [hasSearchedOnce, setHasSearchedOnce] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const onMount = () => {
        const restaurant: RestaurantType = getNewRestaurant();
        setRestaurants([restaurant]);
        setEditedRestaurants([restaurant.id]);
    };

    useEffect(() => {
        onMount();
    }, []);

    const history = useHistory();

    useEffect(() => {
        return history.listen(() => {
            onMount();
            setAcceptedFleetOptions([]);
            setExistingProviders([]);
            setHasSearchedOnce(false);
            setIsFormValidated(false);
            setShowCompletePage(false);
        });
    }, [history]);

    const addRestaurant = () => {
        const restaurant: RestaurantType = getNewRestaurant();
        setRestaurants((list) => [...list, restaurant]);
        setEditedRestaurants((list) => [...list, restaurant.id]);
    };

    const updateRestaurant = (restaurant: RestaurantType) =>
        setRestaurants((list: RestaurantType[]) =>
            list.map((item: RestaurantType) => (restaurant.id === item.id ? restaurant : item))
        );

    useEffect(
        () =>
            setIsFormValidated(
                !restaurants.some(
                    ({ zipCodes, name }) =>
                        !name ||
                        name.trim().length === 0 ||
                        !zipCodes ||
                        zipCodes.trim().length == 0
                )
            ),
        [restaurants]
    );

    const onToggleEditClick = (editId: string) =>
        setEditedRestaurants((list: string[]) =>
            editedRestaurants.includes(editId)
                ? list.filter((id) => id !== editId)
                : [...list, editId]
        );

    const onRemoveClick = (removeId: string) => {
        setRestaurants((list) => list.filter(({ id }) => removeId !== id));
        setEditedRestaurants((list) => list.filter((id) => removeId !== id));
    };

    const showDetails = (item: FleetCoverageDetails) => {
        setDetailsItem(item);
        setShowingDetails(true);
    };

    const fetchFleetAvailability = useCallback(async () => {
        setLoading(true);

        const newAcceptedOptions = await fetchAvailability(
            graph,
            role.id,
            restaurants,
            editedRestaurants,
            acceptedFleetOptions,
            updateRestaurant
        );

        setEditedRestaurants([]);
        setAcceptedFleetOptions(newAcceptedOptions);
        setLoading(false);
        setHasSearchedOnce(true);
    }, [restaurants, editedRestaurants, acceptedFleetOptions]);

    const updateAcceptedFleetOptions = useCallback(
        (restaurantId: string, newOptions: AcceptedFleetOptions) => {
            const newAcceptedOptions = acceptedFleetOptions.filter(
                (fleetOptions: AcceptedFleetOptions) => fleetOptions.restaurantId !== restaurantId
            );
            newAcceptedOptions.push(newOptions);
            setAcceptedFleetOptions(newAcceptedOptions);
        },
        [acceptedFleetOptions]
    );

    const onSubmitForm = async () => {
        const success = await submitForm(
            graph,
            role.id,
            restaurants,
            user.contact?.email || '',
            existingProviders,
            acceptedFleetOptions
        );

        window.history.replaceState(null, 'Thank you!', '/delivery-service-providers/thank-you');
        setShowCompletePage(!!success);
    };

    const canSubmit =
        editedRestaurants.length === 0 &&
        restaurants.find((restaurant) => restaurant?.fleetOptions?.length > 0);

    const allAcceptedFleetNames = [];
    acceptedFleetOptions.map((acceptedFleetOption) =>
        allAcceptedFleetNames.push(...acceptedFleetOption.acceptedFleetNames)
    );

    if (showCompletePage) {
        return (
            <Flex horizontal="center">
                <Flex direction="column" style={styles.container}>
                    <Container style={styles.contentContainer}>
                        <CompletePage {...{ acceptedFleetOptions }} />
                    </Container>
                </Flex>
            </Flex>
        );
    }

    return (
        <Flex horizontal="center">
            <Flex direction="column" style={styles.container}>
                <Container style={styles.contentContainer}>
                    <HeaderContent />
                    <OverlayablePanel active={loading} hideText>
                        {restaurants.map((restaurant, index) => (
                            <RestaurantResult
                                key={restaurant.id}
                                entryNumber={index}
                                onChange={updateRestaurant}
                                editMode={editedRestaurants.includes(restaurant.id)}
                                canRemove={restaurants.length > 1}
                                {...{
                                    restaurant,
                                    showDetails,
                                    onToggleEditClick,
                                    onRemoveClick,
                                    updateAcceptedFleetOptions,
                                    acceptedFleetOptions
                                }}
                            />
                        ))}
                        <HelpSection />
                        {editedRestaurants.length > 0 && (
                            <Flex horizontal="between">
                                {isFormValidated ? (
                                    <a onClick={addRestaurant}>{t('findFleets.addRestaurant')}</a>
                                ) : (
                                    <div />
                                )}
                                {!hasSearchedOnce && (
                                    <ConditionalTooltip
                                        label={
                                            !isFormValidated ? t('findFleets.submitWarning') : ''
                                        }>
                                        <Button
                                            type="submit"
                                            onClick={fetchFleetAvailability}
                                            disabled={!isFormValidated}>
                                            {t('findFleets.findAvailableFleet')}
                                        </Button>
                                    </ConditionalTooltip>
                                )}
                            </Flex>
                        )}
                        {canSubmit && (
                            <>
                                <ChooseActivePartnerships
                                    {...{ restaurants, existingProviders, setExistingProviders }}
                                />
                                <Flex horizontal="right">
                                    <ConditionalTooltip
                                        label={
                                            allAcceptedFleetNames?.length === 0
                                                ? t('findFleets.noFleetSelected')
                                                : ''
                                        }>
                                        <Button
                                            style={styles.submitButton}
                                            type="submit"
                                            onClick={onSubmitForm}
                                            disabled={allAcceptedFleetNames?.length === 0}>
                                            {t('findFleets.activate')}
                                        </Button>
                                    </ConditionalTooltip>
                                </Flex>
                            </>
                        )}
                    </OverlayablePanel>
                </Container>
            </Flex>
            <FleetCoverageDetailsModal
                {...{ isShowingDetails, detailsItem }}
                onRequestClose={() => setShowingDetails(false)}
            />
            {editedRestaurants.length > 0 && hasSearchedOnce && (
                <div style={styles.floatingButton}>
                    <ConditionalTooltip
                        label={!isFormValidated ? t('findFleets.submitWarning') : ''}>
                        <Button
                            type="submit"
                            onClick={fetchFleetAvailability}
                            disabled={!isFormValidated}>
                            {t('findFleets.saveAndFind')}
                        </Button>
                    </ConditionalTooltip>
                </div>
            )}
        </Flex>
    );
};

export default FindFleets;
